import { createContext, ReactNode, useCallback, useContext, useEffect, useRef, useState } from "react";
import { httpService } from "../services/httpService";
import { API_CONFIG, DUPLICATE_CHECK_DAILY_COUNT, LOCAL_STORAGE_KEYS } from "../shared/constants";
import { DuplicateAppGroup } from "../features/Configurations/Integration/interface";
import { useAuth } from "../hooks/helper";
import DuplicateAppsWizardModal from "../features/Configurations/Integration/components/DuplicateAppsWizardModal";

interface DuplicateAppsContextValue {
  duplicateGroups: DuplicateAppGroup[];
  hasDuplicates: boolean;
  isCheckingDuplicates: boolean;
  openModal: () => Promise<void>;
  checkOnPageLoad: () => Promise<void>;
}

const DuplicateAppsContext = createContext<DuplicateAppsContextValue>({
  duplicateGroups: [],
  hasDuplicates: false,
  isCheckingDuplicates: false,
  openModal: async () => {},
  checkOnPageLoad: async () => {},
});

export function DuplicateAppsProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [duplicateGroups, setDuplicateGroups] = useState<DuplicateAppGroup[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCheckingDuplicates, setIsCheckingDuplicates] = useState(false);
  const isRunningRef = useRef(false);

  const checkIntervalMs = Math.floor((24 * 60 * 60 * 1000) / DUPLICATE_CHECK_DAILY_COUNT);

  const fetchDuplicates = async (): Promise<DuplicateAppGroup[]> => {
    return httpService.get<DuplicateAppGroup[]>(API_CONFIG.path.duplicateApps);
  };

  // True when the user has clicked "Resolve Later" and the scheduled cycle hasn't reset yet.
  // Suppression is cleared by runPeriodicCheck at the start of each new scheduled cycle.
  const isAutoOpenSuppressed = (): boolean => {
    return !!localStorage.getItem(LOCAL_STORAGE_KEYS.DUPLICATE_APPS_USER_DISMISSED);
  };

  // Called on App List page mount — ALWAYS fetches fresh data.
  // Updates hasDuplicates (button visibility) on every call.
  // Auto-opens the modal only when the user hasn't dismissed it this cycle.
  const checkOnPageLoad = useCallback(async () => {
    if (isRunningRef.current) return;
    isRunningRef.current = true;
    try {
      const groups = await fetchDuplicates();
      setDuplicateGroups(groups);
      if (groups.length > 0 && !isAutoOpenSuppressed()) {
        setIsModalOpen(true);
      }
    } catch {
      // fail silently
    } finally {
      isRunningRef.current = false;
    }
  }, []);

  // Background scheduled check — runs on mount and on the periodic timer.
  // Clears the dismissal suppression flag at the start of every new cycle so that
  // the next App List page open will auto-open the modal again.
  // Does NOT auto-open the modal itself — that is exclusively checkOnPageLoad's job.
  const runPeriodicCheck = useCallback(async () => {
    if (isRunningRef.current) return;
    isRunningRef.current = true;
    try {
      const lastCheckStr = localStorage.getItem(LOCAL_STORAGE_KEYS.DUPLICATE_APPS_LAST_CHECK);
      const now = Date.now();
      if (lastCheckStr && now - parseInt(lastCheckStr, 10) < checkIntervalMs) {
        return;
      }
      // New scheduled cycle — clear any prior dismissal so the next page load can auto-open
      localStorage.removeItem(LOCAL_STORAGE_KEYS.DUPLICATE_APPS_USER_DISMISSED);
      localStorage.setItem(LOCAL_STORAGE_KEYS.DUPLICATE_APPS_LAST_CHECK, String(now));
      const groups = await fetchDuplicates();
      setDuplicateGroups(groups.length > 0 ? groups : []);
    } catch {
      // fail silently
    } finally {
      isRunningRef.current = false;
    }
  }, [checkIntervalMs]);

  // Manually triggered by the "Resolve Duplicate Apps" button — always fetches and opens.
  const openModal = useCallback(async () => {
    setIsCheckingDuplicates(true);
    try {
      const groups = await fetchDuplicates();
      setDuplicateGroups(groups);
      if (groups.length > 0) {
        setIsModalOpen(true);
      }
    } catch {
      // fail silently
    } finally {
      setIsCheckingDuplicates(false);
    }
  }, []);

  // Records dismissal so checkOnPageLoad won't auto-open again until the next scheduled cycle.
  const handleClose = useCallback(() => {
    localStorage.setItem(LOCAL_STORAGE_KEYS.DUPLICATE_APPS_USER_DISMISSED, String(Date.now()));
    setIsModalOpen(false);
  }, []);

  const handleResolved = useCallback((resolvedStoreIds: string[]) => {
    setDuplicateGroups((prev) => {
      const remaining = prev.filter(
        (g) => !resolvedStoreIds.includes(g.google_admob_store_id)
      );
      if (remaining.length === 0) {
        setIsModalOpen(false);
      }
      return remaining;
    });
  }, []);

  useEffect(() => {
    if (!user) return;
    runPeriodicCheck();
    const interval = setInterval(runPeriodicCheck, checkIntervalMs);
    return () => clearInterval(interval);
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <DuplicateAppsContext.Provider
      value={{
        duplicateGroups,
        hasDuplicates: duplicateGroups.length > 0,
        isCheckingDuplicates,
        openModal,
        checkOnPageLoad,
      }}
    >
      {children}
      <DuplicateAppsWizardModal
        isOpen={isModalOpen}
        groups={duplicateGroups}
        onClose={handleClose}
        onResolved={handleResolved}
      />
    </DuplicateAppsContext.Provider>
  );
}

export function useDuplicateApps() {
  return useContext(DuplicateAppsContext);
}
